import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { devLog } from "@/lib/devLog";
import { trackEvent } from "@/lib/metaPixel";
import { trackLinkedInConversion } from "@/lib/linkedInInsight";
import { INITIAL_CONTACT_FORM } from "@/components/site/contact/contactOptions";

// Encapsulates state, validation, and submission for the contact form.
export const useContactForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL_CONTACT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const toggleArrayField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((x) => x !== value)
        : [...prev[field], value],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("contact.errors.required");
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = t("contact.errors.validEmail");
    if (!form.company.trim()) e.company = t("contact.errors.required");
    if (!form.role.trim()) e.role = t("contact.errors.required");
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = t("contact.errors.minMessage");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error(t("contact.errors.pleaseFill"));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, source_page: "contact" };
      const res = await submitContact(payload);
      setSuccess(res);
      setForm(INITIAL_CONTACT_FORM);
      toast.success(t("contact.errors.inquiryReceived"));
      // Meta Pixel Lead + CAPI mirror. Advanced Matching data boosts EMQ.
      const nameParts = (payload.name || "").trim().split(/\s+/);
      const firstName = nameParts[0] || undefined;
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : undefined;
      trackEvent(
        "Lead",
        {
          event_id: res?.reference,
          content_name: "Professional Inquiry",
          content_category: "contact",
          reference: res?.reference,
        },
        {
          email: payload.email,
          phone: payload.phone,
          firstName,
          lastName,
          externalId: res?.reference,
        }
      );
      // LinkedIn Insight Tag — Lead conversion mirror. Fires only if the
      // visitor has granted marketing consent (the trackLinkedInConversion
      // helper hard-guards on that). Passing `null` sends an untyped Lead
      // event; swap in a Campaign Manager Conversion ID once you create
      // one (Insight Tag → Conversions → Create Conversion → numeric ID).
      trackLinkedInConversion(null);
    } catch (err) {
      devLog.warn("Contact submit failed", err);
      toast.error(err?.response?.data?.detail || t("contact.errors.submitFailed"));
    } finally {
      setLoading(false);
    }
  };

  const resetSuccess = () => setSuccess(null);

  return {
    form,
    errors,
    loading,
    success,
    setField,
    toggleArrayField,
    handleSubmit,
    resetSuccess,
  };
};
